import * as React from 'react';
import IUser from '../model/IUser';
import { session, ISessionState } from './session';

export interface IAuthContextValue {
    user?: IUser;
    isAuthenticated: boolean;
    /** True until the initial silent refresh has settled. */
    initializing: boolean;
    login: (username: string, password: string) => Promise<IUser>;
    logout: () => Promise<void>;
}

const AuthContext = React.createContext<IAuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, setState] = React.useState<ISessionState>(() => session.getState());

    React.useEffect(() => {
        const unsubscribe = session.subscribe(setState);
        void session.initialize();
        return unsubscribe;
    }, []);

    const value = React.useMemo<IAuthContextValue>(() => ({
        user: state.user,
        isAuthenticated: !!state.accessToken && !!state.user,
        initializing: state.initializing,
        login: (username, password) => session.login(username, password),
        logout: () => session.logout(),
    }), [state]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): IAuthContextValue {
    const context = React.useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used inside <AuthProvider>.');
    }
    return context;
}
